import React, { useEffect, useState } from "react"
import { View } from "react-native"
import GoldTrophy from "@/assets/clipart/gold-standard-trophy.svg"
import DoubleDouble from "@/assets/clipart/double-double-bills.svg"
import DoubleDoubleDesat from "@/assets/clipart/double-double-bills-desat.svg"
import RefiKeys from "@/assets/clipart/refi-keys.svg"
import RefiKeysDesat from "@/assets/clipart/refi-keys-desat.svg"
import Rocket from "@/assets/clipart/smaller-rocket.svg"
import RocketDesat from "@/assets/clipart/smaller-rocket-desat.svg"
import { useEditLoan } from "@/queries/loan"
import { showErrorToast } from "@/components/toasts/AppToast"
import {
  type CombinedLoanAndProductsQuery,
  type ProductType,
} from "@/generated/graphql"
import {
  type CapitalProductSlug,
  getCapitalProductDescriptionBySlug,
  getCapitalProductNameBySlug,
} from "@/screens/loans/utils/LoanCapitalProducts"
import { Body, H2, H6, Tiny } from "@/components/standard/BFText"
import { loanUpdatingState } from "@/stores/updateLoan"
import { capitalProductCardClicked } from "@/analytics/events/loans"
import { LoanProductCardRequirements } from "@/screens/loans/components/LoanProductCardRequirements"
import { SecondaryLightButton } from "@/components/buttons/CommonButtons"
import { FlipView } from "@/components/FlipView"
import LoanSelectedIcon from "@/assets/icons/loan-selected.svg"
import ToolLine from "@/components/util/ToolLine"
import { numberToCurrency } from "@/utils/NumberUtils"
import SubSelectedIcon from "@/assets/icons/capital-product-check-teal.svg"
import { useLoanContext } from "@/screens/loans/components/LoanContext"

interface LoanProductCardProps {
  cardIndex: number
  loanId: string
  cardData: ProductType
  loanData: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>
  isSelected: boolean
}

function ProductClipArt({
  slug,
  selectable,
}: {
  slug: CapitalProductSlug
  selectable: boolean
}) {
  switch (slug) {
    case "standard":
      return (
        <GoldTrophy
          width={71 * 1.3}
          height={124 * 1.3}
          style={{ width: 71 * 1.3, height: 124 * 1.3 }}
        />
      )

    case "zero_gravity":
      return selectable ? (
        <Rocket width={104} height={104} style={{ width: 104, height: 104 }} />
      ) : (
        <RocketDesat style={{ width: 104, height: 104 }} />
      )

    case "refinance_rehab": {
      const scaleConstant = 1.3
      return selectable ? (
        <RefiKeys
          style={{ width: 71 * scaleConstant, height: 124 * scaleConstant }}
        />
      ) : (
        <RefiKeysDesat
          style={{ width: 71 * scaleConstant, height: 124 * scaleConstant }}
        />
      )
    }
  }

  // Double-double, default
  return selectable ? (
    <DoubleDouble style={{ width: 104, height: 104 }} />
  ) : (
    <DoubleDoubleDesat style={{ width: 104, height: 104 }} />
  )
}

const LoanProductCard: React.FC<LoanProductCardProps> = ({
  cardIndex,
  loanId,
  cardData,
  loanData,
  isSelected,
}) => {
  const [selected, setSelected] = useState<boolean>(isSelected)
  const [showRequirements, setShowRequirements] = useState<boolean>(false)
  const editLoanMutation = useEditLoan()
  const [loading, setIsLoading] = useState(false)

  if (!cardData) {
    return null
  }

  const productSlug = cardData.product as CapitalProductSlug

  const title: string = getCapitalProductNameBySlug(productSlug) || "N/A"
  const description: string =
    getCapitalProductDescriptionBySlug(productSlug) || "N/A"
  const ltcAtCompletion = cardData.loanToCostAtCompletion
    ? Math.round(cardData.loanToCostAtCompletion * 100 || 0)
    : 0
  const interestRate: number | null | undefined = cardData.interestRate
    ? cardData.interestRate * 100 || 0.0
    : 0
  const originationFee: number | null | undefined = cardData.originationFee
    ? cardData.originationFee * 100
    : 0
  const isQualified = cardData.isEligible || false
  const isSelectable = cardData.isSelectable || false

  useEffect(() => {
    setSelected(isSelected)
  }, [isSelected])

  const handleSelectionToggle = () => {
    if (loading) return // Prevent multiple clicks during loading

    if (selected) return // Temporary until BE can handle null https://dobackflip.atlassian.net/browse/EN-4642

    setIsLoading(true)
    loanUpdatingState.set(true)

    let productSlugUpdate: CapitalProductSlug | null

    // Toggle productSlug between the selected product and null
    if (!selected) {
      productSlugUpdate = cardData.product as CapitalProductSlug
    } else {
      productSlugUpdate = null // Reset productSlug when deselecting
    }

    editLoanMutation.mutate(
      {
        loanId,
        loanInput: {
          loanProduct: productSlugUpdate,
          leverageDiscount: null, // Always set leverageDiscount to null
        },
      },
      {
        onSuccess: () => {
          loanUpdatingState.set(false)
          setIsLoading(false)
          capitalProductCardClicked(
            cardIndex,
            getCapitalProductNameBySlug(productSlug),
            loanData
          )
        },
        onError: (error) => {
          setIsLoading(false)
          loanUpdatingState.set(false)
          setSelected(false) // Revert selection on error
          console.error("Error updating loan product:", error)
          const displayMessage =
            "An error occurred while updating the loan product. Please try again."
          showErrorToast({
            title: "Selection Error",
            message: displayMessage,
          })
        },
      }
    )
    setSelected(!selected) // Optimistic UI update
  }

  function handleShowRequirements() {
    setShowRequirements(true)
  }

  const { cashToCloseDisplaysAsRange } = useLoanContext()
  const c2cIsZero: boolean = cardData.cashToClose === 0

  return (
    <View className={"w-full flex-1 min-h-[288px]"}>
      <FlipView
        back={
          <LoanProductCardRequirements
            cardData={cardData}
            loanData={loanData}
            isSelected={isSelected}
            onHide={() => {
              setShowRequirements(false)
            }}
          />
        }
        isFlipped={showRequirements}
      >
        <View
          className={`flex-1 w-full p-5 bg-grey border rounded border-light-grey overflow-hidden flex-col relative`}
        >
          <View className="flex-row pt-2">
            <H2
              className={`${isSelectable ? "text-midnight-blue" : "text-dark-grey"} mr-2 mb-1`}
            >
              {title}
            </H2>
            {isQualified && (
              <View
                style={{
                  backgroundColor: "#2ADC83",
                  marginVertical: 4,
                  marginLeft: 5,
                }}
                className={`px-2 justify-center items-center`} // bg-green-500
              >
                <H6 color={"default"}>ELIGIBLE</H6>
              </View>
            )}
          </View>

          <Body className="text-midnight-blue">{description}</Body>

          <View className={`flex-row justify-between flex-1`}>
            <View className="flex-1 justify-center pt-4 gap-3 max-w-[172px]">
              <CardDetailItem
                isSelectable={isSelectable}
                subtitle="LTC"
                value={ltcAtCompletion}
                unit="%"
              />
              <CardDetailItem
                isSelectable={isSelectable}
                subtitle="Interest"
                value={interestRate.toFixed(2)}
                unit="%"
              />
              <CardDetailItem
                isSelectable={isSelectable}
                subtitle="Origination"
                value={originationFee.toFixed(2)}
                unit="%"
              />
              {!c2cIsZero && (
                <>
                  <ToolLine />
                  <CardDetailItem
                    isSelectable={isSelectable}
                    subtitle={`Cash to Close ${cashToCloseDisplaysAsRange ? "Estimate" : ""}`}
                    secondLine={cashToCloseDisplaysAsRange}
                    value={
                      cashToCloseDisplaysAsRange
                        ? `${numberToCurrency(cardData.cashToCloseRangeMin || 0)} - ${numberToCurrency(cardData.cashToCloseRangeMax || 0)}`
                        : numberToCurrency(cardData.cashToClose || 0)
                    }
                  />
                </>
              )}
            </View>

            <View className={"flex-1 justify-center items-end pr-4"}>
              <ProductClipArt slug={productSlug} selectable={isSelectable} />
            </View>
          </View>

          <View className={"mt-2 justify-center items-center"}>
            <SecondaryLightButton
              testID={
                selected
                  ? "selected-view"
                  : isSelectable
                    ? "requirements-button"
                    : "select-product-button"
              }
              loading={loading}
              selected={selected}
              style={{
                cursor: "auto",
              }}
              size={"sm"}
              className={"w-full"}
              title={
                selected
                  ? "Selected"
                  : !isSelectable
                    ? "See requirements needed"
                    : "Select"
              }
              icon={SubSelectedIcon}
              onPress={
                !isSelectable ? handleShowRequirements : handleSelectionToggle // Always allow toggling
              }
            />
          </View>
          {isSelected && (
            <View className={"absolute top-4 right-4"}>
              <LoanSelectedIcon width={24} height={24} />
            </View>
          )}
        </View>
      </FlipView>
    </View>
  )
}

export default LoanProductCard

interface CardDetailItemProps {
  subtitle: string
  value: number | string
  isSelectable: boolean
  secondLine?: boolean
  unit?: string
}

const CardDetailItem: React.FC<CardDetailItemProps> = ({
  subtitle,
  value,
  isSelectable,
  secondLine = false,
  unit = null,
}) => {
  return (
    <View className={secondLine ? "flex-col" : "flex-row justify-between"}>
      <Tiny className="text-midnight-blue text-[12px] mb-0">{subtitle}</Tiny>
      <View className={secondLine ? "items-start" : "flex-row items-center"}>
        <H6
          className={`${isSelectable ? "text-midnight-blue" : "text-dark-grey"}`}
        >
          {value}
        </H6>
        {unit !== null && (
          <H6
            className={`${isSelectable ? "text-midnight-blue" : "text-dark-grey"} ${
              secondLine ? "mt-1" : "ml-1"
            }`}
          >
            {unit}
          </H6>
        )}
      </View>
    </View>
  )
}
